import lang from 'lang'
import { isObject } from 'lodash'
import request from '@/modules/base/util/request'
import settingsConfig from 'settings/config'
import { subscriptionLimits } from '@/modules/base/util/constants'
import { UNSAFE_openUpgradeModal as openUpgradeModal } from '@/hooks/useUpgradeModal'

import { toast } from '@/modules/base/components/toast/toastConfig'

// actions
const SHOPTYPES_LOADED = 'settings/ecommerce/SHOPTYPES_LOADED'
const SHOPS_LOADED = 'settings/ecommerce/SHOPS_LOADED'
const SHOPSETTINGS_LOADED = 'settings/ecommerce/SHOPSETTINGS_LOADED'
const SHOW_LOADER = 'settings/ecommerce/SHOW_LOADER'
const HIDE_LOADER = 'settings/ecommerce/HIDE_LOADER'
const SHOW_MODAL_LOADER = 'settings/ecommerce/SHOW_MODAL_LOADER'
const HIDE_MODAL_LOADER = 'settings/ecommerce/HIDE_MODAL_LOADER'
const OPEN_SHOP_FORM_MODAL = 'settings/ecommerce/OPEN_SHOP_FORM_MODAL'
const CLOSE_SHOP_FORM_MODAL = 'settings/ecommerce/CLOSE_SHOP_FORM_MODAL'
const SET_ECOMMERCE_SETTING = 'settings/ecommerce/SET_ECOMMERCE_SETTING'

// reducer
const initialState = {
  isLoading: false,
  showModalLoader: false,
  shopFormModalOpen: false,
  shopTypes: [],
  shopSettings: undefined,
  shops: [],
  selectedShop: undefined,
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SHOW_LOADER:
      return { ...state, isLoading: true }

    case SHOPTYPES_LOADED:
      const { shopTypes } = action
      return { ...state, shopTypes, isLoading: false }

    case SHOPS_LOADED:
      const { shops } = action
      return { ...state, shops, isLoading: false }

    case SHOPSETTINGS_LOADED:
      const { shopSettings } = action
      return { ...state, shopSettings, isLoading: false }

    case HIDE_LOADER:
      return { ...state, isLoading: false }

    case SHOW_MODAL_LOADER:
      return { ...state, showModalLoader: true }

    case HIDE_MODAL_LOADER:
      return { ...state, showModalLoader: false }

    case OPEN_SHOP_FORM_MODAL:
      const { selectedShop } = action
      return { ...state, shopFormModalOpen: true, selectedShop }

    case CLOSE_SHOP_FORM_MODAL:
      return { ...state, shopFormModalOpen: false, selectedShop: undefined }

    case SET_ECOMMERCE_SETTING:
      const { data } = action
      return { ...state, ...data }

    default:
      return state
  }
};

// action creators
const showLoader = () => {
  return {
    type: SHOW_LOADER,
  }
}

const shopTypesLoaded = (data) => {
  return {
    type: SHOPTYPES_LOADED,
    shopTypes: data,
  }
}

const shopsLoaded = (data) => {
  return {
    type: SHOPS_LOADED,
    shops: data,
  }
}

const shopSettingsLoaded = (data) => {
  return {
    type: SHOPSETTINGS_LOADED,
    shopSettings: data,
  }
}

const hideLoader = () => {
  return {
    type: HIDE_LOADER,
  }
}

const showModalLoader = () => {
  return {
    type: SHOW_MODAL_LOADER,
  }
}

const hideModalLoader = () => {
  return {
    type: HIDE_MODAL_LOADER,
  }
}

const setEcommerceSetting = (data) => {
  return {
    type: SET_ECOMMERCE_SETTING,
    data,
  }
}

export const openShopFormModal = (shop) => ({
  type: OPEN_SHOP_FORM_MODAL,
  selectedShop: shop,
})

export const closeShopFormModal = () => ({
  type: CLOSE_SHOP_FORM_MODAL,
})

export const fetchShopTypes = () => {
  return (dispatch) => {
    dispatch(showModalLoader())
    request(`${settingsConfig.ecommerce}/types`, { auth: true, method: 'GET', isConcurrent: true })
      .then((result) => {
        const { body: { data } } = result
        dispatch(shopTypesLoaded(data))
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.shopFetchErrorMessage })
      })
      .finally(() => dispatch(hideModalLoader()))
  }
}

export const fetchShopSettings = (id) => {
  return (dispatch) => {
    dispatch(showLoader())
    Promise.all([
      request(settingsConfig.ecommerce, { auth: true, method: 'GET', isConcurrent: true }),
      request(`${settingsConfig.ecommerce}/${id}`, { auth: true, method: 'GET', isConcurrent: true }),
    ])
      .then((result) => {
        const { body: data } = result
        dispatch(shopSettingsLoaded(data))
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.shopFetchErrorMessage })
      })
      .finally(() => dispatch(hideLoader()))
  }
}

export const createShop = (data) => {
  return (dispatch, getState) => {
    dispatch(showModalLoader())
    request(settingsConfig.ecommerce, { auth: true, method: 'POST', data })
      .then((response) => {
        const { body: { data } } = response
        const { settings: { ecommerce: { shops } } } = getState()
        shops.push(data)

        dispatch(setEcommerceSetting({ shops }))
        dispatch(closeShopFormModal())
        toast.showToast({ message: lang.shopCreateSuccessMessage })
      })
      .catch((error) => {
        let errorMessage = lang.defaultErrorMessage

        const { settings: { ecommerce: { shops } } } = getState()
        if (error?.body?.meta?.[subscriptionLimits.SHOP_COUNT]) {
          if (shops?.length) {
            openUpgradeModal({
              limitReached: subscriptionLimits.SHOP_COUNT,
              subscriptionChange: true,
            })
          } else {
            openUpgradeModal({
              limitReached: subscriptionLimits.SHOP_CONNECTION_NOTINPLAN,
              subscriptionChange: true,
            })
          }
        } else {
          if (error?.body?.message?.code === 'INVALID') {
            errorMessage = lang.eCommerce.shopDataInvalidErrorMessage
          } else if (error?.body?.message?.code === 'EXISTS') {
            errorMessage = lang.eCommerce.shopAlreadyExistsErrorMessage
          } else if (error?.body?.message?.msg) {
            errorMessage = error?.body?.message?.msg
          }

          toast.showToast({ message: errorMessage, type: 'error' })
        }
      })
      .done(() => dispatch(hideModalLoader()))
  }
}

export const getShops = () => {
  return (dispatch) => {
    request(`${settingsConfig.ecommerce}`, { auth: true, method: 'GET', isConcurrent: true })
      .then((response) => {
        const { body: { data } } = response
        dispatch(shopsLoaded(data))
      })
      .catch((error) => {
        toast.showToast({ message: isObject(error) ? error.error.message : error, type: 'error' })
      })
  }
}

export const updateShop = (id, data) => {
  return (dispatch, getState) => {
    dispatch(showModalLoader())
    request(`${settingsConfig.ecommerce}/${id}`, { auth: true, method: 'PUT', data })
      .then(() => {
        const { settings: { ecommerce: { shops } } } = getState()
        const updatedShops = shops.map(shop => {
          return shop.id === id ? { ...shop, ...data } : shop
        })

        dispatch(setEcommerceSetting({ shops: updatedShops }))
        dispatch(closeShopFormModal())
        dispatch(hideModalLoader())
        toast.showToast({ message: lang.shopSaveSuccessMessage })
      })
      .catch((error) => {
        dispatch(hideModalLoader())
        toast.showToast({ message: isObject(error) ? error.error.message : error, type: 'error' })
      })
  }
}

export const deleteShop = (id) => {
  return (dispatch, getState) => {
    dispatch(showLoader())
    request(`${settingsConfig.ecommerce}/${id}`, { method: 'DELETE', auth: true })
      .then(() => {
        const { settings: { ecommerce: { shops } } } = getState()
        const filteredShops = shops.filter(shop => shop.id !== id)

        dispatch(setEcommerceSetting({ shops: filteredShops }))
        toast.showToast({ message: lang.shopDeleteSuccessMessage })
      })
      .catch(() => {
        toast.showToast({ type: 'error', message: lang.shopDeleteErrorMessage })
      })
      .done(() => {
        dispatch(hideLoader())
      })
  }
}
